import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { agentApiFixed as agentApi } from "@/lib/api/agent-api-fixed";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Loader2, Save, History, RefreshCw } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { format } from "date-fns";
import { AgentPrompt } from "@/types/agent-types";

export const AgentPromptEditor = () => {
  const [selectedAgentType, setSelectedAgentType] = useState<string>("");
  const [promptContent, setPromptContent] = useState<string>("");
  const [isPromptHistoryOpen, setIsPromptHistoryOpen] = useState(false);
  const [promptHistory, setPromptHistory] = useState<AgentPrompt[]>([]);
  const queryClient = useQueryClient();

  // Fetch available agent types
  const {
    data: agentTypes = [],
    isLoading: isLoadingAgentTypes,
    isFetching: isFetchingAgentTypes,
    error: agentTypesError,
    refetch: refetchAgentTypes,
  } = useQuery({
    queryKey: ["agentTypes"],
    queryFn: () => agentApi.getAgentTypes(),
  });

  // Fetch latest prompt when agent type changes
  const {
    data: latestPrompt,
    isLoading: isLoadingPrompt,
    isFetching: isFetchingPrompt,
    isError: isPromptError,
    error: promptError,
    refetch: refetchLatestPrompt,
  } = useQuery({
    queryKey: ["latestPrompt", selectedAgentType],
    queryFn: () => agentApi.getLatestPrompt(selectedAgentType),
    enabled: !!selectedAgentType,
    retry: false,
  });

  const canEditPrompt = !!selectedAgentType && !!latestPrompt && !isFetchingPrompt && !isPromptError;
  const isBlankPrompt = !promptContent.trim();

  // Update prompt content when latestPrompt changes
  useEffect(() => {
    setPromptContent(latestPrompt?.prompt ?? "");
  }, [latestPrompt, selectedAgentType]);

  // Mutation for saving a new prompt
  const savePromptMutation = useMutation({
    mutationFn: ({ agentType, prompt }: { agentType: string; prompt: string }) =>
      agentApi.savePrompt(agentType, prompt),
  });

  // Fetch prompt history
  const fetchPromptHistory = async () => {
    if (!selectedAgentType) return;
    try {
      const history = await agentApi.getPromptHistory(selectedAgentType);
      setPromptHistory(history);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch prompt history",
        variant: "destructive",
      });
    }
  };


  const handleSavePrompt = async () => {
    if (!canEditPrompt || isBlankPrompt) return;

    try {
      await savePromptMutation.mutateAsync({
        agentType: selectedAgentType,
        prompt: promptContent,
      });
      toast({
        title: "Success",
        description: "Prompt saved successfully",
        variant: "default",
      });
      queryClient.invalidateQueries({ queryKey: ["latestPrompt", selectedAgentType] });
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to save prompt: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  const handleAgentTypeChange = (value: string) => {
    setPromptContent("");
    setPromptHistory([]);
    setIsPromptHistoryOpen(false);
    setSelectedAgentType(value);
  };

  const handleApplyHistoricalPrompt = (prompt: AgentPrompt) => {
    setPromptContent(prompt.prompt);
    setIsPromptHistoryOpen(false);
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Agent Prompt Editor</CardTitle>
        <CardDescription>
          Create and edit prompts for different agent types
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="agent-type" className="text-sm font-medium">
            Agent Type
          </label>
          <div className="flex gap-2">
            <Select value={selectedAgentType} onValueChange={handleAgentTypeChange} disabled={isLoadingAgentTypes}>
              <SelectTrigger id="agent-type">
                <SelectValue placeholder="Select an agent type" />
              </SelectTrigger>
              <SelectContent>
                {agentTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="icon"
              disabled={!selectedAgentType || isFetchingPrompt}
              onClick={() => refetchLatestPrompt()}
              title="Refresh prompt"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
          {agentTypesError && (
            <div role="alert" className="space-y-2 text-sm text-destructive">
              <p className="break-words">
                Agent types could not be loaded: {agentTypesError instanceof Error ? agentTypesError.message : String(agentTypesError)}
              </p>
              <Button type="button" variant="outline" size="sm" disabled={isFetchingAgentTypes} onClick={() => refetchAgentTypes()}>
                Retry
              </Button>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label htmlFor="prompt" className="text-sm font-medium">
              Prompt Template
            </label>
            <Dialog open={isPromptHistoryOpen} onOpenChange={setIsPromptHistoryOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!canEditPrompt}
                  onClick={fetchPromptHistory}
                  className="flex items-center gap-1 text-xs h-8"
                >
                  <History className="h-3.5 w-3.5" />
                  Prompt History
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Prompt History</DialogTitle>
                  <DialogDescription>
                    Previous versions of the prompt for {selectedAgentType}
                  </DialogDescription>
                </DialogHeader>
                <div className="max-h-[60vh] overflow-y-auto">
                  {promptHistory.length > 0 ? (
                    <div className="space-y-4">
                      {promptHistory.map((prompt) => (
                        <div
                          key={prompt._id}
                          className="border rounded-md p-4 hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium">
                              Version: {prompt.version}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {format(
                                new Date(prompt.created_at),
                                "MMM d, yyyy h:mm a"
                              )}
                            </span>
                          </div>
                          <div className="max-h-32 overflow-y-auto bg-muted/30 rounded p-2 text-xs font-mono mb-2">
                            <pre className="whitespace-pre-wrap">{prompt.prompt}</pre>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleApplyHistoricalPrompt(prompt)}
                          >
                            Use This Version
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">
                      No prompt history available
                    </p>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <Textarea
            id="prompt"
            value={isPromptError || !latestPrompt ? "" : promptContent}
            onChange={(e) => setPromptContent(e.target.value)}
            disabled={!canEditPrompt}
            placeholder={
              isPromptError
                ? "Prompt could not be loaded"
                : isLoadingPrompt
                  ? "Loading prompt..."
                  : selectedAgentType
                    ? "Enter prompt text"
                    : "Select an agent type to edit its prompt"
            }
            className="min-h-[400px] font-mono text-sm"
          />
          {isPromptError && (
            <p role="alert" className="text-sm text-destructive">
              Failed to load prompt: {promptError.message}
            </p>
          )}
          {canEditPrompt && isBlankPrompt && (
            <p role="alert" className="text-sm text-destructive">
              Enter prompt text before saving.
            </p>
          )}
        </div>
      </CardContent>
      <CardFooter className="flex justify-end">
        <Button
          onClick={handleSavePrompt}
          disabled={
            !canEditPrompt ||
            isBlankPrompt ||
            savePromptMutation.isPending ||
            promptContent === latestPrompt?.prompt
          }
        >
          {savePromptMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save Prompt
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};
